/**
 * The Reorder chart sheet's draft, shared by both apps. The sheet edits a list
 * of section entries, each holding its row entries (existing sections and rows,
 * copies to create, ones to delete), and applies it to the current sections on
 * Apply. The sheet shows the draft as one flat list of section headers and the
 * rows of expanded sections (`flattenDraft`), since neither app's drag library
 * can move an item between nested lists.
 */
import { DEFAULT_TIME_SIGNATURE, getRowMeters } from './barBeats.js';
import { mergeRowInto } from './barEditing.js';
import { getRowSlots } from './barUtils.js';
import { getChordDisplayText } from './customChordUtils.js';
import { normalizeSongTimeSignature } from './songMeta.js';
export const draftFromSections = (sections) => sections.map(section => ({
    key: section.id,
    sourceId: section.id,
    rows: section.rows.map(row => ({ key: row.id, sourceRowId: row.id })),
}));
export const isDraftCopy = (entry) => entry.key !== entry.sourceId;
export const isRowCopy = (entry) => entry.key !== entry.sourceRowId;
/** The rows a row entry stands for: its own, then those merged into it, in the order their bars land. */
export const draftRowSourceIds = (entry) => [
    entry.sourceRowId,
    ...(entry.merged ?? []).flatMap(draftRowSourceIds),
];
/** Whether the entry carries an existing row (itself or one merged into it), which Apply must account for. */
export const holdsOriginalRow = (entry) => !isRowCopy(entry) || (entry.merged ?? []).some(holdsOriginalRow);
/** Whether a section entry carries an existing section or row. */
const holdsOriginal = (entry) => !isDraftCopy(entry) || entry.rows.some(holdsOriginalRow);
/** A copy of a row entry, merged rows included, under fresh keys. */
const copyRowEntry = (entry, newKey) => ({
    key: newKey(),
    sourceRowId: entry.sourceRowId,
    ...(entry.merged?.length ? { merged: entry.merged.map(m => copyRowEntry(m, newKey)) } : {}),
});
/** The sections the sheet shows: everything not deleted. */
export const visibleDraft = (draft) => draft.filter(entry => !entry.removed);
/** A section's rows the sheet shows: everything not deleted. */
export const visibleRows = (section) => section.rows.filter(row => !row.removed);
/**
 * Moves `id` by `delta` places (negative = up), clamped to the list. Returns
 * the same array when nothing moves, so callers can skip a no-op commit.
 */
export const moveIdBy = (ids, id, delta) => {
    const from = ids.indexOf(id);
    if (from === -1)
        return ids;
    const to = Math.max(0, Math.min(ids.length - 1, from + delta));
    if (to === from)
        return ids;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, id);
    return next;
};
/** Puts the visible sections in the order of `keys`; deleted entries stay at the end. */
export const setDraftOrder = (draft, keys) => {
    const byKey = new Map(draft.map(entry => [entry.key, entry]));
    const ordered = keys.flatMap(key => {
        const entry = byKey.get(key);
        return entry && !entry.removed ? [entry] : [];
    });
    return [...ordered, ...draft.filter(entry => entry.removed)];
};
/** Moves the section `key` by `delta` places among the visible sections. */
export const moveInDraft = (draft, key, delta) => {
    const keys = visibleDraft(draft).map(entry => entry.key);
    const moved = moveIdBy(keys, key, delta);
    return moved === keys ? draft : setDraftOrder(draft, moved);
};
/**
 * Deletes the section `key`: a copy simply leaves the draft; an existing
 * section is marked `removed` (and moved last) so Apply deletes it. So is a
 * copy holding a row moved in from elsewhere, so Apply deletes that row
 * rather than take it for one the sheet never saw.
 */
export const removeFromDraft = (draft, key) => {
    const entry = draft.find(e => e.key === key);
    if (!entry || entry.removed)
        return draft;
    const rest = draft.filter(e => e.key !== key);
    return holdsOriginal(entry) ? [...rest, { ...entry, removed: true }] : rest;
};
/**
 * Inserts a copy of the section `key` right after it. Its rows become copies
 * of the section's current draft rows; `newKey` returns a key unique in the
 * draft on each call.
 */
export const duplicateInDraft = (draft, key, newKey) => {
    const index = draft.findIndex(entry => entry.key === key);
    if (index === -1 || draft[index].removed)
        return draft;
    const source = draft[index];
    const copy = {
        key: newKey(),
        sourceId: source.sourceId,
        rows: visibleRows(source).map(row => copyRowEntry(row, newKey)),
    };
    const next = [...draft];
    next.splice(index + 1, 0, copy);
    return next;
};
/** Where the row `key` is: its section's index in the draft, and its index among that section's visible rows. */
export const findRow = (draft, key) => {
    for (let sectionIndex = 0; sectionIndex < draft.length; sectionIndex++) {
        const section = draft[sectionIndex];
        if (section.removed)
            continue;
        const rowIndex = visibleRows(section).findIndex(row => row.key === key);
        if (rowIndex !== -1)
            return { sectionIndex, rowIndex };
    }
    return null;
};
/** A section's rows with `visible` as its visible rows; its deleted entries stay last. */
const withVisibleRows = (section, visible) => ({
    ...section,
    rows: [...visible, ...section.rows.filter(row => row.removed)],
});
/**
 * Moves the row `key` into the section `toSectionKey`, at `toIndex` among its
 * visible rows (clamped). Refused when it would leave its section empty.
 */
export const moveRowTo = (draft, key, toSectionKey, toIndex) => {
    const at = findRow(draft, key);
    const targetIndex = draft.findIndex(section => section.key === toSectionKey && !section.removed);
    if (!at || targetIndex === -1)
        return { draft };
    const from = draft[at.sectionIndex];
    const fromVisible = visibleRows(from);
    const entry = fromVisible[at.rowIndex];
    if (targetIndex === at.sectionIndex) {
        const rest = fromVisible.filter(row => row.key !== key);
        const to = Math.max(0, Math.min(rest.length, toIndex));
        if (to === at.rowIndex)
            return { draft };
        rest.splice(to, 0, entry);
        return { draft: draft.map((section, i) => (i === at.sectionIndex ? withVisibleRows(section, rest) : section)) };
    }
    if (fromVisible.length === 1)
        return { draft, refused: 'lastRow' };
    const targetVisible = [...visibleRows(draft[targetIndex])];
    targetVisible.splice(Math.max(0, Math.min(targetVisible.length, toIndex)), 0, entry);
    return {
        draft: draft.map((section, i) => {
            if (i === at.sectionIndex)
                return withVisibleRows(section, fromVisible.filter(row => row.key !== key));
            if (i === targetIndex)
                return withVisibleRows(section, targetVisible);
            return section;
        }),
    };
};
/**
 * Moves the row `key` one place up or down (the screen-reader move). At its
 * section's edge it crosses into the neighbouring visible section: to the end
 * of the one above, or the start of the one below.
 */
export const stepRow = (draft, key, delta) => {
    const at = findRow(draft, key);
    if (!at)
        return { draft };
    const section = draft[at.sectionIndex];
    const count = visibleRows(section).length;
    const target = at.rowIndex + delta;
    if (target >= 0 && target < count)
        return moveRowTo(draft, key, section.key, target);
    const sections = visibleDraft(draft);
    const neighbour = sections[sections.findIndex(s => s.key === section.key) + delta];
    if (!neighbour)
        return { draft };
    return moveRowTo(draft, key, neighbour.key, delta === -1 ? visibleRows(neighbour).length : 0);
};
/**
 * Inserts a copy of the row `key` (with what was merged into it) right under
 * it; `newKey` returns a key unique in the draft on each call.
 */
export const duplicateRowInDraft = (draft, key, newKey) => {
    const at = findRow(draft, key);
    if (!at)
        return draft;
    const visible = [...visibleRows(draft[at.sectionIndex])];
    visible.splice(at.rowIndex + 1, 0, copyRowEntry(visible[at.rowIndex], newKey));
    return draft.map((section, i) => (i === at.sectionIndex ? withVisibleRows(section, visible) : section));
};
/**
 * Deletes the row `key`: a copy simply leaves the draft; an existing row (or a
 * copy holding one merged into it) is marked `removed` (and moved last).
 * Refused for a section's last row.
 */
export const removeRowFromDraft = (draft, key) => {
    const at = findRow(draft, key);
    if (!at)
        return { draft };
    const section = draft[at.sectionIndex];
    const visible = visibleRows(section);
    if (visible.length === 1)
        return { draft, refused: 'lastRow' };
    const entry = visible[at.rowIndex];
    const rest = visible.filter(row => row.key !== key);
    const removed = section.rows.filter(row => row.removed);
    const rows = holdsOriginalRow(entry) ? [...rest, ...removed, { ...entry, removed: true }] : [...rest, ...removed];
    return { draft: draft.map((s, i) => (i === at.sectionIndex ? { ...s, rows } : s)) };
};
/**
 * Where "Move chords to previous row" would put the row `key`: the row above
 * it, or the last row of the visible section above. Null for the chart's
 * first row.
 */
export const previousRowKey = (draft, key) => {
    const at = findRow(draft, key);
    if (!at)
        return null;
    const section = draft[at.sectionIndex];
    if (at.rowIndex > 0)
        return visibleRows(section)[at.rowIndex - 1].key;
    const sections = visibleDraft(draft);
    const above = sections[sections.findIndex(s => s.key === section.key) - 1];
    if (!above)
        return null;
    const rows = visibleRows(above);
    return rows.length > 0 ? rows[rows.length - 1].key : null;
};
/**
 * "Move chords to previous row": the row `key` leaves its section and rides
 * in the previous row's entry (`merged`), whose bars it joins on Apply, like
 * the chart's own row merge. Refused when it is its section's last row.
 */
export const mergeRowIntoPrevious = (draft, key) => {
    const at = findRow(draft, key);
    const targetKey = previousRowKey(draft, key);
    if (!at || !targetKey)
        return { draft };
    const visible = visibleRows(draft[at.sectionIndex]);
    if (visible.length === 1)
        return { draft, refused: 'lastRow' };
    const entry = visible[at.rowIndex];
    return {
        draft: draft.map((section, i) => {
            const rows = i === at.sectionIndex ? section.rows.filter(row => row.key !== key) : section.rows;
            if (!rows.some(row => row.key === targetKey))
                return i === at.sectionIndex ? { ...section, rows } : section;
            return {
                ...section,
                rows: rows.map(row => (row.key === targetKey ? { ...row, merged: [...(row.merged ?? []), entry] } : row)),
            };
        }),
    };
};
/** A list key unique across sections and rows. */
export const flatItemKey = (item) => `${item.type}:${item.key}`;
/** The sheet's list: every visible section header, followed by its rows when expanded. */
export const flattenDraft = (draft, expanded) => visibleDraft(draft).flatMap(section => [
    { type: 'section', key: section.key },
    ...(expanded.has(section.key)
        ? visibleRows(section).map(row => ({ type: 'row', key: row.key, sectionKey: section.key }))
        : []),
]);
/**
 * Turns the drag library's reordered list back into a draft. A dropped row
 * belongs to the nearest section header above it (the first section when
 * dropped above every header), at its place among that section's rows (the end
 * when the section is collapsed); a move that would empty its section is
 * refused. A dropped section takes its place among the headers; rows always
 * stay with their own section, so it can never land between another section's
 * rows. `landedSectionKey` is where a dropped row ended up.
 */
export const dropInFlatList = (draft, expanded, items, moved) => {
    if (moved.type === 'section') {
        return { draft: setDraftOrder(draft, items.filter(item => item.type === 'section').map(item => item.key)) };
    }
    const index = items.findIndex(item => item.type === 'row' && item.key === moved.key);
    if (index === -1)
        return { draft };
    let headerIndex = -1;
    for (let i = index - 1; i >= 0; i--) {
        if (items[i].type === 'section') {
            headerIndex = i;
            break;
        }
    }
    if (headerIndex === -1) {
        const first = items.find(item => item.type === 'section');
        if (!first)
            return { draft };
        return { ...moveRowTo(draft, moved.key, first.key, 0), landedSectionKey: first.key };
    }
    const target = items[headerIndex].key;
    const toIndex = expanded.has(target) ? index - headerIndex - 1 : Number.MAX_SAFE_INTEGER;
    return { ...moveRowTo(draft, moved.key, target, toIndex), landedSectionKey: target };
};
const sameMeter = (a, b) => a.numerator === b.numerator && a.denominator === b.denominator;
/**
 * Keeps every row in the meter it had in `before`. A row's meter carries from
 * the last signature at or above it, so moving rows or sections can change
 * the meter of moved rows and of the rows around where they left or landed;
 * split bars sized for the old meter would then play differently. Walking
 * `after` in reading order, a row without its own signature whose carried
 * meter differs from its old one gets its old meter written on it. A copy
 * keeps its source row's meter (`sourceRowIdOf`). With a global time
 * signature every row shares it and nothing changes.
 */
export const preserveRowMeters = (before, after, globalTimeSignature, sourceRowIdOf = () => undefined) => {
    if (globalTimeSignature)
        return after;
    const oldMeters = getRowMeters(before, null);
    let carried = DEFAULT_TIME_SIGNATURE;
    return after.map(section => {
        let changed = false;
        const rows = section.rows.map(row => {
            const own = normalizeSongTimeSignature(row.timeSignature);
            if (own) {
                carried = own;
                return row;
            }
            const old = oldMeters.get(sourceRowIdOf(row.id) ?? row.id);
            if (!old || sameMeter(old, carried))
                return row;
            carried = old;
            changed = true;
            return { ...row, timeSignature: { numerator: old.numerator, denominator: old.denominator } };
        });
        return changed ? { ...section, rows } : section;
    });
};
/**
 * The current sections laid out as the draft says: sections and rows in its
 * order, copies made with `copyRow` (a section copy takes a fresh id from
 * `newSectionId`), deleted ones left out, rows merged into another appended
 * to it (`mergeRowInto`, with the meters both rows had), and every row kept
 * in its meter (`preserveRowMeters`). Existing rows are looked up chart-wide, since a row
 * may have moved to another section. An edit that lands while the sheet is
 * open (cloud sync, say) is kept: entries whose section or row is gone are
 * dropped, and rows or sections the sheet never saw keep their place at the
 * end of their section, or of the chart. A section never comes out empty.
 */
export const applyChartDraft = (sections, draft, { newSectionId, copyRow, globalTimeSignature = null, generateId }) => {
    const sectionsById = new Map(sections.map(section => [section.id, section]));
    const rowsById = new Map(sections.flatMap(section => section.rows.map(row => [row.id, row])));
    const seenSectionIds = new Set(draft.filter(entry => !isDraftCopy(entry)).map(entry => entry.sourceId));
    const originalsOf = (row) => [
        ...(isRowCopy(row) ? [] : [row.sourceRowId]),
        ...(row.merged ?? []).flatMap(originalsOf),
    ];
    const seenRowIds = new Set(draft.flatMap(entry => entry.rows.flatMap(originalsOf)));
    const oldMeters = getRowMeters(sections, globalTimeSignature);
    const meterOf = (rowId) => oldMeters.get(rowId) ?? DEFAULT_TIME_SIGNATURE;
    const copySources = new Map();
    const placed = new Set();
    /** The entry's row (an existing one, or a copy), with any merged rows' bars appended. */
    const buildRow = (rowEntry) => {
        const source = rowsById.get(rowEntry.sourceRowId);
        if (!source)
            return null;
        let row = source;
        if (isRowCopy(rowEntry))
            row = copyRow(source);
        else
            placed.add(source.id);
        for (const mergedEntry of rowEntry.merged ?? []) {
            const merged = buildRow(mergedEntry);
            if (merged) {
                row = mergeRowInto(row, merged, meterOf(source.id), meterOf(mergedEntry.sourceRowId), generateId);
            }
        }
        if (isRowCopy(rowEntry))
            copySources.set(row.id, source.id);
        return row;
    };
    const next = visibleDraft(draft).flatMap(entry => {
        const source = sectionsById.get(entry.sourceId);
        if (!source)
            return [];
        const rows = visibleRows(entry).flatMap(rowEntry => {
            const row = buildRow(rowEntry);
            return row ? [row] : [];
        });
        if (!isDraftCopy(entry)) {
            const unseen = source.rows.filter(row => !seenRowIds.has(row.id));
            unseen.forEach(row => placed.add(row.id));
            rows.push(...unseen);
        }
        if (rows.length === 0) {
            // Only an edit made elsewhere can empty a section: keep what it has now
            const current = isDraftCopy(entry) ? [] : source.rows.filter(row => !placed.has(row.id));
            if (current.length === 0)
                return [];
            current.forEach(row => placed.add(row.id));
            rows.push(...current);
        }
        return [isDraftCopy(entry) ? { ...source, id: newSectionId(), rows } : { ...source, rows }];
    });
    next.push(...sections.filter(section => !seenSectionIds.has(section.id)));
    return preserveRowMeters(sections, next, globalTimeSignature, id => copySources.get(id));
};
export const isSameOrder = (a, b) => a.length === b.length && a.every((section, index) => section.id === b[index].id);
/**
 * Compares the sections before and after an Apply. Precedence: a move, then a
 * delete, then a merge, then a copy; rows before sections. A row "moved" when
 * it changed section or its place among its section's surviving rows. A row
 * that is gone but whose slots live on in another row was merged into it (the
 * slots are only seen when the rows carry their bars).
 */
export const describeChartChange = (before, after) => {
    const beforeIds = new Set(before.map(s => s.id));
    const afterIds = new Set(after.map(s => s.id));
    const beforeRowIds = new Set(before.flatMap(s => s.rows.map(r => r.id)));
    const afterRowSection = new Map(after.flatMap(s => s.rows.map(r => [r.id, s.id])));
    const afterSlotIds = new Set(after.flatMap(s => s.rows.flatMap(r => (r.bars ?? []).flatMap(b => b.slots.map(slot => slot.id)))));
    const wasMerged = (row) => !!row.bars?.length && row.bars.every(b => b.slots.every(slot => afterSlotIds.has(slot.id)));
    const keptRowsOf = (sections) => sections
        .filter(s => beforeIds.has(s.id) && afterIds.has(s.id))
        .map(s => `${s.id}:${s.rows.filter(r => beforeRowIds.has(r.id) && afterRowSection.has(r.id)).map(r => r.id).join(',')}`)
        .sort()
        .join('|');
    const rowsMoved = before.some(s => s.rows.some(r => afterRowSection.has(r.id) && afterRowSection.get(r.id) !== s.id)) ||
        keptRowsOf(before) !== keptRowsOf(after);
    if (rowsMoved)
        return { kind: 'reorder', level: 'row' };
    const kept = (sections) => sections.filter(s => beforeIds.has(s.id) && afterIds.has(s.id));
    if (!isSameOrder(kept(before), kept(after)))
        return { kind: 'reorder', level: 'section' };
    const goneRows = before.filter(s => afterIds.has(s.id)).flatMap(s => s.rows.filter(r => !afterRowSection.has(r.id)));
    if (goneRows.some(r => !wasMerged(r)))
        return { kind: 'delete', level: 'row' };
    if (before.some(s => !afterIds.has(s.id)))
        return { kind: 'delete', level: 'section' };
    if (goneRows.length > 0)
        return { kind: 'merge', level: 'row' };
    if (after.some(s => beforeIds.has(s.id) && s.rows.some(r => !beforeRowIds.has(r.id)))) {
        return { kind: 'duplicate', level: 'row' };
    }
    if (after.some(s => !beforeIds.has(s.id)))
        return { kind: 'duplicate', level: 'section' };
    return null;
};
/** A one-line description of a row for the sheet: its chords, else its lyrics, else empty. */
export const rowLabel = (row) => {
    const slots = getRowSlots(row);
    const chords = slots.filter(slot => slot.chord || slot.customText).map(getChordDisplayText);
    if (chords.length > 0)
        return { kind: 'chords', text: chords.join('  ') };
    const lyrics = slots.map(slot => slot.lyrics?.trim()).filter(Boolean).join(' ');
    if (lyrics)
        return { kind: 'lyrics', text: lyrics };
    return { kind: 'empty', text: '' };
};
